import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, KeyRound, ShieldOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/helpers";

const ROLES = [
  { value: "OWNER", label: "Owner" },
  { value: "SERVICE_MANAGER", label: "Service manager" },
  { value: "VIEWER", label: "Visualizzatore" },
];

export default function UsersAdminPage() {
  const [users, setUsers] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openReset, setOpenReset] = useState(null);
  const [form, setForm] = useState({ username: "", nome: "", role: "VIEWER", initial_password: "" });
  const [resetPwd, setResetPwd] = useState("");

  const load = async () => setUsers(await api.get("/auth/users"));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.username || !form.nome || form.initial_password.length < 8) { toast.error("Compila tutti i campi (password min 8)"); return; }
    try {
      await api.post("/auth/users", form);
      toast.success("Utente creato. Al primo accesso dovrà cambiare password.");
      setOpenNew(false); setForm({ username: "", nome: "", role: "VIEWER", initial_password: "" });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const toggleDisabled = async (u) => {
    await api.put(`/auth/users/${u.id}`, { disabled: !u.disabled });
    toast.success(u.disabled ? "Utente riattivato" : "Utente disabilitato");
    await load();
  };
  const updateRole = async (u, role) => {
    await api.put(`/auth/users/${u.id}`, { role });
    toast.success("Ruolo aggiornato");
    await load();
  };
  const doReset = async () => {
    if (resetPwd.length < 8) { toast.error("Password min 8 caratteri"); return; }
    await api.post(`/auth/users/${openReset.id}/reset-password`, { new_password: resetPwd });
    toast.success("Password resettata. L'utente dovrà cambiarla al prossimo accesso.");
    setOpenReset(null); setResetPwd("");
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-slate-500">Gestione utenti e ruoli · solo OWNER</div>
        <Button onClick={() => setOpenNew(true)} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-new-user">
          <Plus className="w-4 h-4 mr-1" /> Nuovo utente
        </Button>
      </div>
      <Card className="card-clean overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Nome</TableHead><TableHead>Username</TableHead><TableHead>Ruolo</TableHead>
            <TableHead>Stato</TableHead><TableHead>Password</TableHead><TableHead>Creato</TableHead><TableHead>Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id} data-testid={`user-row-${u.username}`}>
                <TableCell className="font-semibold">{u.nome}</TableCell>
                <TableCell className="text-sm">{u.username}</TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={v => updateRole(u, v)}>
                    <SelectTrigger className="w-[170px]" data-testid={`role-${u.username}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch checked={!u.disabled} onCheckedChange={() => toggleDisabled(u)} />
                    <span className="text-xs">{u.disabled ? "Disabilitato" : "Attivo"}</span>
                  </div>
                </TableCell>
                <TableCell>{u.must_change_password && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">Da cambiare</span>}</TableCell>
                <TableCell className="text-xs text-slate-500">{fmtDateTime(u.created_at)}</TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" onClick={() => setOpenReset(u)} data-testid={`btn-reset-${u.username}`}>
                    <KeyRound className="w-4 h-4 mr-1" /> Reset password
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent><DialogHeader><DialogTitle>Nuovo utente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} data-testid="new-user-nome" /></div>
            <div><Label>Username (email)</Label><Input type="email" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} data-testid="new-user-email" /></div>
            <div><Label>Ruolo</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Password iniziale (min 8)</Label><Input type="text" value={form.initial_password} onChange={e => setForm({ ...form, initial_password: e.target.value })} data-testid="new-user-pwd" /></div>
            <div className="text-xs text-slate-500">L'utente dovrà cambiare password al primo accesso.</div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpenNew(false)}>Annulla</Button><Button onClick={create} data-testid="new-user-save">Crea</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!openReset} onOpenChange={o => !o && setOpenReset(null)}>
        <DialogContent><DialogHeader><DialogTitle>Reset password · {openReset?.nome}</DialogTitle></DialogHeader>
          <div><Label>Nuova password iniziale</Label><Input type="text" value={resetPwd} onChange={e => setResetPwd(e.target.value)} /></div>
          <div className="text-xs text-slate-500 mt-2">L'utente dovrà cambiarla al prossimo accesso.</div>
          <DialogFooter><Button variant="outline" onClick={() => setOpenReset(null)}>Annulla</Button><Button onClick={doReset}>Reset</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
